import type ServiceApi from "../../api";
import {ErrorI18n} from "../../i18n";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import {ValidationErrorI18n} from "../validation";
import {WebError} from "../web-error";

/** Result of an ImmoDrop extraction: the extracted payload in AVM-API import format. */
export type ImmoDropResult = Record<string, unknown>;

/**
 * Fachliche Statuscodes des ImmoDrop-Dienstes (WksnErrorType.code) und der i18n-Schlüssel, unter dem
 * REVA sie erklärt. Bewusst einzeln statt als ein Sammeltext "keine Daten erkannt" (#19159): 602
 * heißt gerade NICHT "nichts gefunden", sondern "Werte erkannt, aber für einen Import nicht
 * ausreichend" — typischerweise, weil die Postleitzahl fehlt. Wer das mit 600 zusammenwirft, sucht
 * den Fehler im PDF statt in der fehlenden Adresse.
 *
 * Der Suffix `_file` gilt für den PDF-Upload, ohne Suffix für den URL-Scan; beide liegen unter
 * `importDialog.immodrop.error`.
 */
const IMMODROP_ERROR_KEYS: Readonly<Record<number, string>> = {
    600: "nodata", // NO_DATA — zur Quelle gibt es (derzeit) gar keine Daten
    601: "implausible", // IMPLAUSIBLE_DATA — Werte erkannt, aber unplausibel
    602: "incomplete", // FILTERED_DATA — Werte erkannt, reichen aber nicht für einen Import
    603: "processing", // FAILED_PROCESSING — Verarbeitung im Dienst fehlgeschlagen
};

/**
 * Bildet eine Fehlerantwort des ImmoDrop-Dienstes auf einen fachlichen Fehler ab.
 *
 * Die Klartextmeldung des Dienstes (`{"error": "..."}`) wird als `serviceMessage` mitgegeben, damit
 * die Oberfläche sie als Zusatzzeile zeigen kann — sie ist die einzige Stelle, an der die konkrete
 * Ursache steht, und erspart im Support den Blick ins Container-Log.
 *
 * @param e Fehler aus der API-Ausführung
 * @param variant `file` für den PDF-Upload, `url` für den URL-Scan
 * @return der fachliche Fehler, oder null, wenn es keiner ist (dann greift die allgemeine Behandlung)
 */
function mapImmodropError(e: Error, variant: "file" | "url"): ErrorI18n | null {
    if (!(e instanceof WebError)) {
        return null;
    }
    const key = IMMODROP_ERROR_KEYS[e.status];
    if (key) {
        const suffix = variant === "file" ? "_file" : "";
        return new ErrorI18n(
            `importDialog.immodrop.error.${key}${suffix}`,
            {serviceMessage: e.getCustomErrorMessage()},
            // 603 ist ein Dienstfehler, den der Benutzer nicht beheben kann — nur dort der Kontakt-Hinweis.
            e.status === 603,
        );
    }
    // Nur der Datei-Upload kann fachlich 400 werfen (keine Datei / kein PDF); beim URL-Scan
    // validiert der Client vorher, ein 400 wäre dort ein echter Programmfehler.
    if (e.status === 400 && variant === "file") {
        return new ErrorI18n("importDialog.immodrop.error.unsupported_file", null, false);
    }
    return null;
}

/**
 * API interface for the URL-based ImmoDrop extraction (GET {@link RevaApi.PATH_IMMODROP}).
 *
 * Validates the target URL and passes it (plus an optional OnDemand timeout) as query parameters.
 */
export class ImmodropUrlApiInterface extends SimpleApiInterface<ImmoDropResult> {
    private readonly targetUrl: string;
    private readonly onDemandTimeoutMs: number;
    private readonly signal?: AbortSignal;

    /**
     * @param targetUrl requested URL of the document to parse
     * @param onDemandTimeoutMs optional timeout for OnDemand-requests
     * @param signal optionales AbortSignal, damit der Benutzer die laufende Extraktion abbrechen kann
     */
    constructor(targetUrl: string, onDemandTimeoutMs = 15000, signal?: AbortSignal) {
        super();
        this.targetUrl = targetUrl;
        this.onDemandTimeoutMs = onDemandTimeoutMs;
        this.signal = signal;
    }

    protected validate(): void {
        if (!this.targetUrl || this.targetUrl.length < 7) {
            throw new ValidationErrorI18n("url", "importDialog.immodrop.error.too_short");
        }
        if (!this.targetUrl.startsWith("https://") && !this.targetUrl.startsWith("http://")) {
            throw new ValidationErrorI18n("url", "importDialog.immodrop.error.invalid_scheme");
        }
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_IMMODROP], {
            url: this.targetUrl,
            onDemandTimeoutMs: this.onDemandTimeoutMs,
        });
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ImmoDropResult> {
        return executor.fetchGET(url, credentials, this.signal);
    }

    protected handleApiErrors(e: Error): ImmoDropResult {
        const mapped = mapImmodropError(e, "url");
        if (mapped) {
            throw mapped;
        }
        return super.handleApiErrors(e);
    }
}

/**
 * API interface for the file-based ImmoDrop extraction (POST {@link RevaApi.PATH_IMMODROP_FILE}).
 *
 * Uploads the PDF as a multipart/form-data body; unlike the URL variant there is nothing to validate.
 */
export class ImmodropFileApiInterface extends SimpleApiInterface<ImmoDropResult> {
    private readonly file: File;
    private readonly signal?: AbortSignal;

    /**
     * @param file PDF file to extract data from
     * @param signal optionales AbortSignal, damit der Benutzer den laufenden Upload abbrechen kann
     */
    constructor(file: File, signal?: AbortSignal) {
        super();
        this.file = file;
        this.signal = signal;
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_IMMODROP_FILE]);
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ImmoDropResult> {
        const formData = new FormData();
        formData.append("file", this.file, this.file.name);
        return executor.fetchPOSTFormData(url, credentials, formData, this.signal);
    }

    protected handleApiErrors(e: Error): ImmoDropResult {
        const mapped = mapImmodropError(e, "file");
        if (mapped) {
            throw mapped;
        }
        return super.handleApiErrors(e);
    }
}
